import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, supabaseConfigValid, supabaseConfigError } from './supabase';
import { identifyUser, resetUser } from './analytics';

export type UserRole = 'admin' | 'scorer' | 'viewer';

export interface UserProfile {
  id: string;
  role: UserRole;
  displayName: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  configError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isScorer: boolean;
  isViewer: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(supabaseConfigError);

  // If configuration is invalid, set error and stop loading
  useEffect(() => {
    if (!supabaseConfigValid) {
      setConfigError(supabaseConfigError);
      setLoading(false);
      return;
    }

    // Only attempt auth if config is valid
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => fetchProfile(session.user.id), 0);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, role, display_name')
        .eq('id', userId)
        .single();

      if (error) throw error;

      const profile: UserProfile = {
        id: data.id,
        role: data.role as UserRole,
        displayName: data.display_name ?? '',
      };
      setProfile(profile);

      // Identify in PostHog after profile is loaded
      identifyUser(data.id, { role: profile.role, displayName: profile.displayName });
    } catch (err) {
      console.error('[Auth] Failed to fetch profile:', err);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error || null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    resetUser();
    setUser(null);
    setProfile(null);
    setSession(null);
  };

  const isAdmin = profile?.role === 'admin';
  const isScorer = profile?.role === 'scorer';
  const isViewer = profile?.role === 'viewer';

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      session,
      loading,
      configError,
      signIn,
      signOut,
      isAdmin,
      isScorer,
      isViewer,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Resolves a display_name to the underlying email via the lookup-user edge function. */
export async function lookupEmailByDisplayName(displayName: string): Promise<string> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/lookup-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${anonKey}`,
    },
    body: JSON.stringify({ displayName }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Invalid username or password' }));
    throw new Error(err.error ?? 'Invalid username or password');
  }

  const data = await res.json();
  if (!data.email) throw new Error('Invalid username or password');
  return data.email;
}
